/**
 * AnalyzerHealthDrawer — the per-analyzer HEALTH / USAGE drill-down (MANAGEMENT 15). A side <Drawer> that,
 * for the selected fleet row, reads GET /management/analyzers/:id/health over a chosen trailing window and
 * composes:
 *   • an identity header (code · libellé · fabricant/modèle · discipline · statut badge + live heartbeat);
 *   • a lab + transport meta strip (which lab, which protocol/transport pinning);
 *   • a window switch (7 j / 30 j / 90 j) that re-windows the health series;
 *   • KPI chips (results, errors, error-rate, avg TAT, avg uptime, snapshot days) tinted by the lib tones;
 *   • three charts (result vs error volume · TAT trend · uptime trend).
 *
 * MONITORING-ONLY. Read-only surface — no mutation control anywhere in the drawer (the per-lab `lis` module
 * owns every instrument write). Full inner DoD: loading / error / cloud-absent / empty-usage all degrade to a
 * calm state; the drawer never crashes on an empty estate. Gated upstream by <PlatformAdminGuard> + the
 * screen's own <ForbiddenState>; the `enabled` flag folds the platform gate in so no fetch fires while
 * closed. Token-driven, AA, FR i18n. Numerics ride .nv-ltr-island so they read identically FR/AR.
 */
import { Badge, Card, Drawer, ErrorState, LoadingState, type BadgeTone } from "@/ui";
import { useT } from "@/app";
import { isManagementAbsent } from "../api";
import { useAnalyzerHealth } from "../screens/analyzers.api";
import type { AnalyzerFleetRow } from "../screens/analyzers.types";
import {
  FLEET_CONFIG,
  type HealthWindow,
  analyzerStatutTone,
  errorRateTone,
  fmtCount,
  fmtPct,
  fmtTat,
  heartbeatGlyph,
  heartbeatTone,
  relativeSince,
  uptimeTone,
} from "../screens/analyzers.lib";
import {
  ResultVolumeChart,
  TatTrendChart,
  UptimeTrendChart,
} from "./AnalyzerHealthCharts";

/** A short, stable lab display label (commercial name → raison sociale → reference → id). */
function labLabel(lab: AnalyzerFleetRow["laboratoire"]): string {
  if (!lab) return "—";
  return lab.nomCommercial || lab.raisonSociale || lab.reference || `#${lab.id}`;
}

/** One KPI chip in the drill-down summary strip (label + big mono value + tone). */
function HealthKpi({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: BadgeTone;
}) {
  const COLOR: Record<BadgeTone, string> = {
    neutral: "var(--nv-text-0)",
    success: "var(--nv-success, var(--nv-normal))",
    warn: "var(--nv-warn, var(--nv-high))",
    danger: "var(--nv-danger, var(--nv-critical))",
    info: "var(--nv-info)",
    normal: "var(--nv-normal)",
    low: "var(--nv-low)",
    high: "var(--nv-high)",
    critical: "var(--nv-critical)",
    hold: "var(--nv-hold)",
  };
  return (
    <div
      style={{
        flex: "1 1 120px",
        display: "flex",
        flexDirection: "column",
        gap: "var(--nv-s-1)",
        padding: "var(--nv-s-3)",
        borderRadius: "var(--nv-r-md)",
        background: "var(--nv-surface-2)",
        border: "1px solid var(--nv-line-faint)",
      }}
    >
      <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-2, var(--nv-slate))" }}>
        {label}
      </span>
      <span
        className="nv-ltr-island"
        style={{
          fontFamily: "var(--nv-font-mono)",
          fontSize: "var(--nv-fs-h3)",
          fontWeight: 600,
          color: COLOR[tone],
        }}
      >
        {value}
      </span>
    </div>
  );
}

/** A single meta row (label · value) in the identity / transport strip. */
function MetaRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--nv-s-3)" }}>
      <span style={{ color: "var(--nv-text-2, var(--nv-slate))", fontSize: "var(--nv-fs-caption)" }}>
        {label}
      </span>
      <span
        className={mono ? "nv-ltr-island" : undefined}
        style={{ fontWeight: 600, ...(mono ? { fontFamily: "var(--nv-font-mono)" } : {}) }}
      >
        {value}
      </span>
    </div>
  );
}

/** The 7 j / 30 j / 90 j window switch — a WAI-ARIA radiogroup segmented control (mirrors MetricsPeriodSwitch). */
function WindowSwitch({
  value,
  onChange,
}: {
  value: HealthWindow;
  onChange: (w: HealthWindow) => void;
}) {
  const { t } = useT();
  return (
    <div
      role="radiogroup"
      aria-label={t("mgmt.analyzers.window.label")}
      style={{
        display: "inline-flex",
        gap: "var(--nv-s-1)",
        padding: "var(--nv-s-1)",
        borderRadius: "var(--nv-r-pill)",
        background: "var(--nv-surface-2)",
        border: "1px solid var(--nv-line-faint)",
      }}
    >
      {FLEET_CONFIG.HEALTH_WINDOWS.map((w) => {
        const selected = w === value;
        return (
          <button
            key={w}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(w)}
            className="nv-focusable"
            style={{
              appearance: "none",
              cursor: "pointer",
              border: "none",
              minHeight: "var(--nv-min-touch)",
              padding: "0 var(--nv-s-3)",
              borderRadius: "var(--nv-r-pill)",
              fontSize: "var(--nv-fs-label)",
              fontWeight: selected ? 600 : 500,
              color: selected ? "var(--nv-white)" : "var(--nv-slate)",
              background: selected ? "var(--nv-azure)" : "transparent",
              transition: "background var(--nv-fast) var(--nv-ease)",
            }}
          >
            {t("mgmt.analyzers.window.days", { n: w })}
          </button>
        );
      })}
    </div>
  );
}

export interface AnalyzerHealthDrawerProps {
  /** The selected fleet row (its identity seeds the header instantly, before the health read resolves). */
  row: AnalyzerFleetRow | null;
  window: HealthWindow;
  onWindowChange: (w: HealthWindow) => void;
  onClose: () => void;
  /** The platform gate ANDed with online — folds into the health read's `enabled`. */
  enabled: boolean;
}

export function AnalyzerHealthDrawer({
  row,
  window,
  onWindowChange,
  onClose,
  enabled,
}: AnalyzerHealthDrawerProps) {
  const { t } = useT();
  const open = row !== null;
  const query = useAnalyzerHealth(row?.id ?? null, window, enabled && open);
  const data = query.data;
  const usage = data?.usage;

  const heading = row
    ? t("mgmt.analyzers.drawer.title", { code: row.code })
    : "";

  return (
    <Drawer open={open} onClose={onClose} title={heading}>
      {row && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          {/* Identity header — seeded from the row instantly (no wait on the health read). */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--nv-s-2)", alignItems: "center" }}>
            <strong style={{ fontSize: "var(--nv-fs-title)" }}>{row.libelle}</strong>
            <Badge tone={analyzerStatutTone(row.statut)} glyph="●">
              {t(`mgmt.analyzers.statut.${row.statut}`)}
            </Badge>
            <Badge tone={heartbeatTone(row.heartbeat.online, row.heartbeat.hasAgent)} glyph={heartbeatGlyph(row.heartbeat.online, row.heartbeat.hasAgent)}>
              {row.heartbeat.online
                ? t("mgmt.analyzers.heartbeat.online")
                : row.heartbeat.hasAgent
                  ? t("mgmt.analyzers.heartbeat.stale")
                  : t("mgmt.analyzers.heartbeat.noAgent")}
            </Badge>
          </div>

          {/* Lab + transport meta strip. */}
          <Card title={t("mgmt.analyzers.drawer.meta")}>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}>
              <MetaRow label={t("mgmt.analyzers.col.lab")} value={labLabel(row.laboratoire)} />
              <MetaRow label={t("mgmt.analyzers.meta.code")} value={row.code} mono />
              <MetaRow
                label={t("mgmt.analyzers.col.model")}
                value={[row.fabricant, row.modele].filter(Boolean).join(" · ") || "—"}
              />
              <MetaRow label={t("mgmt.analyzers.col.discipline")} value={row.disciplineCode ?? "—"} mono />
              <MetaRow
                label={t("mgmt.analyzers.col.protocol")}
                value={row.transport.protocole ?? "—"}
                mono
              />
              <MetaRow
                label={t("mgmt.analyzers.meta.transport")}
                value={row.transport.transport ?? "—"}
                mono
              />
              <MetaRow
                label={t("mgmt.analyzers.col.lastSeen")}
                value={relativeSince(row.heartbeat.lastHeartbeatAt)}
              />
            </div>
          </Card>

          {/* Window switch + the health/usage body. */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <WindowSwitch value={window} onChange={onWindowChange} />
          </div>

          {query.isLoading && <LoadingState />}

          {!query.isLoading && isManagementAbsent(query.error) && (
            <Card title={t("mgmt.unavailable.title")}>
              <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>
                {t("mgmt.unavailable.desc")}
              </p>
            </Card>
          )}

          {!query.isLoading && query.isError && !isManagementAbsent(query.error) && (
            <ErrorState
              message={query.error.message}
              code={query.error.code}
              onRetry={() => void query.refetch()}
            />
          )}

          {!query.isLoading && !query.isError && usage && (
            <>
              {/* KPI chips (usage rollup) — every figure tone-tinted by the lib. */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--nv-s-3)" }}>
                <HealthKpi label={t("mgmt.analyzers.kpi.results")} value={fmtCount(usage.resultTotal)} tone="info" />
                <HealthKpi
                  label={t("mgmt.analyzers.kpi.errors")}
                  value={fmtCount(usage.errorTotal)}
                  tone={usage.errorTotal > 0 ? "warn" : "neutral"}
                />
                <HealthKpi
                  label={t("mgmt.analyzers.kpi.errorRate")}
                  value={fmtPct(usage.errorRatePct)}
                  tone={errorRateTone(usage.errorRatePct)}
                />
                <HealthKpi label={t("mgmt.analyzers.kpi.tat")} value={fmtTat(usage.avgTatMs)} tone="neutral" />
                <HealthKpi
                  label={t("mgmt.analyzers.kpi.uptime")}
                  value={fmtPct(usage.avgUptimePct)}
                  tone={uptimeTone(usage.avgUptimePct)}
                />
                <HealthKpi
                  label={t("mgmt.analyzers.kpi.snapshotDays")}
                  value={fmtCount(usage.snapshotDays)}
                  tone="neutral"
                />
              </div>

              {/* Charts. */}
              <Card title={t("mgmt.analyzers.chart.volume.title")}>
                <ResultVolumeChart series={usage.series} />
              </Card>
              <Card title={t("mgmt.analyzers.chart.tat.title")}>
                <TatTrendChart series={usage.series} />
              </Card>
              <Card title={t("mgmt.analyzers.chart.uptime.title")}>
                <UptimeTrendChart series={usage.series} />
              </Card>
            </>
          )}
        </div>
      )}
    </Drawer>
  );
}
